import { useFormik } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'

import {
  Box,
  Button,
  Center,
  Container,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  UseModalProps,
  useSteps,
} from '@masterplandev/ui'

import { usePrevious } from '@/core/hooks/usePrevious'
import useServerResponseToast from '@/core/hooks/useServerResponseToast'
import useTranslation from '@/core/hooks/useTranslation'

import { usePollResultsCreate } from '../../mutations/usePollResultsCreate'
import { usePollDetails } from '../../queries/usePollDetails'
import { PollFormError } from './PollFormError'
import { PollFormHeader } from './PollFormHeader'
import { PollFormProgress } from './PollFormProgress'
import { PollFormStep } from './PollFormStep'

function StepsTransition({ children, step }) {
  const previousStep = usePrevious(step)
  const motionDirection = step > previousStep ? 1 : -1

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 600 : -600,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 600 : -600,
      opacity: 0,
    }),
  }

  return (
    <Box w="full">
      <AnimatePresence initial={false} custom={motionDirection}>
        <motion.div
          layout
          key={step}
          custom={motionDirection}
          variants={variants}
          style={{ position: 'relative' }}
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 29 },
            opacity: { duration: 0.2 },
          }}
          initial="enter"
          animate="center"
          exit="exit">
          <Box position="absolute" w="full" left="0" right="0">
            {children}
          </Box>
        </motion.div>
      </AnimatePresence>
    </Box>
  )
}

type PollFormProps = UseModalProps & {
  pollId: string
  children: React.ReactNode
}

export function PollForm({ pollId, children, ...modalProps }: PollFormProps) {
  const { translate } = useTranslation('poll-form')
  const toast = useServerResponseToast('department.toast.create')
  const pollDetails = usePollDetails({ pollId })
  const pollResultsCreate = usePollResultsCreate()
  const [isValid, setIsValid] = useState(false)
  const scrollElementRef = useRef<HTMLDivElement>(null)

  // steps state
  const countFormSteps = pollDetails.data?.sections?.length || 0
  const countAllSteps = countFormSteps + 1
  const lastFormStep = countFormSteps - 1
  const lastStep = countAllSteps - 1
  const steps = useSteps({ count: countAllSteps })

  // form state
  const form = useFormik({
    initialValues: {},
    onSubmit: (values) => {
      pollResultsCreate.mutate(
        {
          params: { pollId },
          data: { answers: values },
        },
        {
          onSuccess: () => {
            scrollElementRef?.current?.scrollTo({ top: 0 })
          },
          onError: (error) => {
            toast.error(error)
          },
        },
      )
    },
  })

  // Ensure form/step state resets even when the parent navigates away before
  // the modal close animation fires onCloseComplete.
  useEffect(() => {
    return () => {
      form.resetForm()
      steps.setActiveStep(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // redirect to last step if survey is completed
  if (
    pollDetails.data?.is_completed === true &&
    steps.activeStep !== lastStep
  ) {
    steps.setActiveStep(lastStep)
  }

  return (
    <Modal
      autoFocus={false}
      scrollBehavior="inside"
      size="full"
      motionPreset="none"
      isOpen={modalProps.isOpen}
      onClose={modalProps.onClose}
      onCloseComplete={() => {
        form.resetForm()
        steps.setActiveStep(0)
      }}>
      <ModalOverlay />
      <ModalContent bg="mp2.neutralDark.0" p="0">
        <ModalHeader px="16" pt="16" pb="4">
          <PollFormHeader>
            {countAllSteps > 1 && (
              <PollFormProgress
                activeStep={steps.activeStep}
                countSteps={countAllSteps}
              />
            )}
          </PollFormHeader>
        </ModalHeader>
        <ModalBody
          ref={scrollElementRef}
          display="flex"
          layerStyle="scroll"
          overflowX="hidden"
          overflowY="scroll"
          sx={{
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'mp2.neutralLight.900',
              border: '7px solid transparent',
              borderRadius: 'full',
              backgroundClip: 'content-box',
            },
          }}
          position="relative"
          color="mp2.neutralLight.100">
          {match(pollDetails)
            .with({ status: 'pending' }, () => (
              <Center w="full">
                <Spinner size="xl" />
              </Center>
            ))
            .with({ status: 'error' }, () => (
              <PollFormError
                onClose={modalProps.onClose}
                onRetry={pollDetails.refetch}
              />
            ))
            .with({ status: 'success' }, ({ data }) => (
              <StepsTransition step={steps.activeStep}>
                <Container maxW="6xl" my="12">
                  {steps.activeStep !== lastStep && (
                    <form onSubmit={form.handleSubmit}>
                      <PollFormStep
                        onValidate={setIsValid}
                        pollSection={data.sections?.[steps.activeStep]}
                        form={form}
                      />
                    </form>
                  )}
                  {steps.activeStep === lastStep && <>{children}</>}
                </Container>
              </StepsTransition>
            ))
            .exhaustive()}
        </ModalBody>

        {steps.activeStep !== lastStep && (
          <ModalFooter justifyContent="space-between" px="16" pb="68px" pt="4">
            <Button
              variant="outline"
              onClick={
                steps.activeStep === 0 ? modalProps.onClose : steps.goToPrevious
              }>
              {translate('go-back')}
            </Button>
            <Button
              type="button"
              isLoading={pollResultsCreate.isPending}
              isDisabled={!isValid}
              onClick={() => {
                if (steps.activeStep === lastFormStep) {
                  form.submitForm()
                } else {
                  scrollElementRef?.current?.scrollTo({ top: 0 })
                  steps.goToNext()
                }
              }}>
              {translate('continue')}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
